import clsx from "clsx";
import type { FC } from "react";
import styled from "styled-components";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  FontVariantEnum,
  type ICustomField,
  type IStyledAttributes,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

import { FieldExplanationIcon } from "./_parts";

const variantBlockClass: Record<FontVariantEnum, string> = {
  [FontVariantEnum.Default]: "group-title--default",
  [FontVariantEnum.Small]: "group-title--small",
  [FontVariantEnum.ExtraSmall]: "group-title--extra-small",
};

const GroupTitleStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "group-title-template",
})<{ $labelColor?: string | null }>`
  position: relative;
  display: flex;
  align-items: center;
  margin: 24px 0 16px;
  padding: 2px;
  width: 100%;

  .group-title__label {
    font-weight: 600;
    margin-inline-end: 10px;
  }

  &.group-title--default .group-title__label {
    font-size: 1.7em;
    color: ${props => props.$labelColor || greyMui._900};
  }

  &.group-title--small .group-title__label {
    font-size: 1.4em;
    color: ${props => props.$labelColor || greyMui._800};
  }

  &.group-title--extra-small .group-title__label {
    font-size: 0.95em;
    color: ${props => props.$labelColor || greyMui._700};
  }
`;

const DashedLineStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "group-title-template",
})`
  border: 1px dashed ${greyMui._400};
  opacity: 0.4;
  flex-grow: 1;
`;

export const GroupTitleTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formLabelColor,
  formIcon,
  formAttributes,
  labelTranslated,
}) => {
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const fontVariant = formAttributes?.fontVariant ?? FontVariantEnum.Default;
  return (
    <GroupTitleStyled
      $labelColor={formLabelColor}
      className={clsx("group-title", variantBlockClass[fontVariant])}
      data-e2e-template="group-title-template"
      data-e2e-field-name={name}
    >
      {formIcon}
      <label className="group-title__label">{formLabelTranslated}</label>
      <DashedLineStyled />
      <FieldExplanationIcon formAttributes={formAttributes} />
    </GroupTitleStyled>
  );
};
